import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { setupWebsite } from '../utils/businessWebsite';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  CloudArrowUpIcon,
  CodeBracketIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  QrCodeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Upload from './rag/Upload';
import WebScraper from './rag/WebScraper';
import PasteContentRecovery from '../components/rag/PasteContentRecovery';
import { agentPublicUrl, hasAgentId } from '../utils/agentLinks';
import ragService from '../services/rag.service';
import { agentsAPI } from '../services/api';
import { renameWorkspace, updateNotificationContacts } from '../services/tenant.service';
import { useAuthStore } from '../store/authStore';
import { trackOnboardingStarted } from '../utils/googleAnalytics';
import { flushOnboardingEvents, setOnboardingStep, trackOnboarding } from '../services/onboardingTelemetry';

// The owner's own first conversation is tagged so Conversations can tell it
// from real customers and a failed first answer stays diagnosable. chat.html
// reads utm_* and the widget sends it with every message. The QR and share
// links stay clean.
const getTrialAgentUrl = (agent) => {
  const publicUrl = agentPublicUrl(agent);
  if (!publicUrl) return undefined;
  const url = new URL(publicUrl);
  url.searchParams.set('utm_source', 'setup_trial');
  url.searchParams.set('utm_campaign', 'first_agent_check');
  return url.toString();
};

const buildWidgetCode = (agentId) => {
  const widgetHost = process.env.REACT_APP_WIDGET_HOST || window.location.origin;
  const scriptUrl = `${widgetHost.replace(/\/$/, '')}/embed/serviti-chat-widget.js`;
  return `<!-- Serviti AI Chat Widget -->\n<script\n  src="${scriptUrl}"\n  data-agent-id="${agentId}"\n></script>`;
};

const downloadText = (filename, text) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

// These are the customer-facing, standalone business templates. Keep role
// templates (lead capture, support, document analysis) out of self-serve
// onboarding: a customer should choose the agent that describes their
// business, not an internal assistant role.
// The agents table holds 100 characters and signup allows a 100-character
// business name, so " AI Agent" is only added when it fits. The first agent is
// created from this without a name field, so an over-long name could never be
// corrected and every retry would fail the same way.
export const AGENT_NAME_MAX = 100;
const AGENT_SUFFIX = ' AI Agent';
export const defaultAgentName = (businessName) => {
  const base = (businessName || '').trim();
  if (!base) return AGENT_SUFFIX.trim();
  if (base.length + AGENT_SUFFIX.length <= AGENT_NAME_MAX) return `${base}${AGENT_SUFFIX}`;
  return base.slice(0, AGENT_NAME_MAX).trim();
};

const ONBOARDING_VERTICALS = [
  { template: 'website_assistant.json', label: 'Other business or general website' },
  { template: 'services_assistant.json', label: 'Professional or local services' },
  { template: 'real_estate_assistant.json', label: 'Real estate' },
  { template: 'school_website_assistant.json', label: 'School or educational institution' },
  { template: 'coaching_tuition_assistant.json', label: 'Coaching or tuition centre' },
  { template: 'clinic_assistant.json', label: 'Clinic or healthcare practice' },
  { template: 'dental_assistant.json', label: 'Dental practice' },
  { template: 'salon_spa_assistant.json', label: 'Salon or spa' },
  { template: 'gym_fitness_assistant.json', label: 'Gym or fitness studio' },
  { template: 'home_services_assistant.json', label: 'Home services' },
  { template: 'auto_dealer_assistant.json', label: 'Automotive dealer or service centre' },
  { template: 'restaurant_assistant.json', label: 'Restaurant' },
  { template: 'cafe_coffee_shop_assistant.json', label: 'Cafe or coffee shop' },
  { template: 'hotel_resort_assistant.json', label: 'Hotel or resort' },
  { template: 'event_venue_assistant.json', label: 'Event venue' },
  { template: 'd2c_product_discovery_assistant.json', label: 'D2C or online retail' },
  { template: 'industrial_assistant.json', label: 'Manufacturing or industrial business' },
];


const PLATFORM_TENANT_ID = '00000000-0000-0000-0000-000000000000';

const FirstTimeSetupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const updateUserProfile = useAuthStore((state) => state.updateUserProfile);
  // Knowledge must be checked before creating an agent.
  const [step, setStep] = useState('knowledge');
  const [source, setSource] = useState(null);
  const [collection, setCollection] = useState(null);
  // An extra agent gets its own collection. What it could share instead, and
  // with whom, stays a choice the customer makes on screen.
  const [ownCollection, setOwnCollection] = useState(null);
  const [sharedOptions, setSharedOptions] = useState([]);
  const [sharedWith, setSharedWith] = useState([]);
  const [unfinished, setUnfinished] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  // Read by what runs after an await, which holds the render it started in.
  const latestCollection = useRef(null);
  latestCollection.current = collection;
  const pendingCollection = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState('website_assistant.json');
  const agentNameRef = useRef(null);
  const [agentName, setAgentName] = useState(() => defaultAgentName(user?.tenant_name));
  const notificationEmail = user?.email || (/\S+@\S+\.\S+/.test(user?.username || '') ? user.username : '');
  const [agent, setAgent] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [capacity, setCapacity] = useState(null);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [knowledgeSummary, setKnowledgeSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [workspaceName, setWorkspaceName] = useState(user?.tenant_name || '');
  const [renaming, setRenaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const requestedAdditionalAgent = new URLSearchParams(location.search).get('create') === '1';
  const isAdditionalAgent = requestedAdditionalAgent && (capacity?.agent_count || 0) > 0;
  const signupWebsite = setupWebsite(onboardingProgress, { additionalAgent: isAdditionalAgent });
  // The platform tenant is not a workspace: a superadmin's collection list
  // spans every tenant, and setup picked Green Wood's and crawled into it.
  const isPlatformWorkspace = user?.tenant_id === PLATFORM_TENANT_ID;

  useEffect(() => {
    if (capacity?.agent_count === 0) trackOnboardingStarted(user?.tenant_id);
  }, [capacity?.agent_count, user?.tenant_id]);

  useEffect(() => {
    let active = true;

    const prepareKnowledgeCollection = async () => {
      if (isPlatformWorkspace) {
        setLoading(false);
        return;
      }
      try {
        const [capacityResponse, progressResponse] = await Promise.all([
          agentsAPI.getCapacity(),
          agentsAPI.getOnboardingStatus(),
        ]);
        const availableCapacity = capacityResponse.data;
        const progress = progressResponse.data || {};
        if (active) setCapacity(availableCapacity);
        if (active) setOnboardingProgress(progress);
        trackOnboarding('wizard_loaded', {
          next_step: progress.next_step,
          knowledge_ready: progress.knowledge_ready,
          agent_ready: progress.agent_ready,
          can_create_agent: availableCapacity?.can_create_agent,
          additional_agent: requestedAdditionalAgent,
        });
        // Returning after the agent was made: bring it back and ask only for
        // what is still missing, rather than starting over.
        if (active && !requestedAdditionalAgent && progress.agent_ready && !progress.knowledge_ready) {
          try {
            const agentsResponse = await agentsAPI.list();
            const existingAgent = agentsResponse.data?.agents?.[0];
            if (active && existingAgent) setAgent(existingAgent);
          } catch {
            // The knowledge step does not need the agent; the finish step
            // looks it up again if this failed.
          }
          if (active) setStep('knowledge');
        }
        // A workspace at its agent limit still needs its knowledge collection
        // when the agent it already has is unfinished. Bailing out here left a
        // returning customer on the limit screen with no way to finish setup.
        if (!availableCapacity?.can_create_agent && (requestedAdditionalAgent || !progress.agent_ready)) return;

        const response = await ragService.getCollections();
        const available = response.collections || [];

        // Reusing the workspace's collection put a villa's menu into the
        // Serviti agent's answers. An extra agent starts on its own knowledge.
        if (requestedAdditionalAgent && (availableCapacity?.agent_count || 0) > 0) {
          const { data: knowledge } = await agentsAPI.getKnowledgeOptions();
          const byId = (id) => available.find((item) => item.id === id);
          const options = (knowledge.shared || [])
            .map(({ collection_id: id, agents }) => ({ collection: byId(id), agents }))
            .filter((option) => option.collection);
          const leftOver = (knowledge.unfinished || [])
            .map(({ collection_id: id, importing }) => ({ collection: byId(id), importing }))
            .filter((item) => item.collection);
          if (active) {
            setSharedOptions(options);
            setUnfinished(leftOver);
            setStep('knowledge');
          }
          return;
        }

        // None yet stays none: the first submission creates it.
        const selected = available.find((item) => (
          progress.knowledge_collection && [
            item.internal_name,
            item.collection_name,
            item.name,
          ].includes(progress.knowledge_collection)
        )) || available.find((item) => item.is_default) || available[0];

        if (active) {
          setCollection(selected || null);
          if (!requestedAdditionalAgent && !progress.knowledge_ready) {
            const supplied = setupWebsite(progress, { additionalAgent: false });
            if (supplied.prefill) setSource('scrape');
            else if (progress.business_description || supplied.unreadable) setSource('paste');
          }
        }
        // Saved but no agent yet: show what it holds, exactly as straight after
        // saving it. Someone who closed the tab before confirming has not
        // confirmed.
        if (active && selected && progress.knowledge_ready && !progress.agent_ready) {
          await showPreview(selected);
        }
      } catch (err) {
        trackOnboarding('wizard_load_failed', {
          status: err.response?.status ?? null,
          detail: err.response?.data?.detail || err.message,
        });
        if (active) {
          setLoadFailed(true);
          setError(err.response?.data?.detail || 'Could not prepare your knowledge base.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    prepareKnowledgeCollection();
    return () => { active = false; };
  }, [requestedAdditionalAgent, user?.tenant_id, user?.tenant_name]);

  // Step 2 is named after the journey taken, not the current screen: once
  // the create screen was used, it stays "Create your agent" after finishing.
  const [usedCreateScreen, setUsedCreateScreen] = useState(false);
  useEffect(() => {
    if (step === 'agent') setUsedCreateScreen(true);
  }, [step]);

  // Which screen someone was on is what separates "left" from "was stopped".
  useEffect(() => {
    if (loading) return;
    setOnboardingStep(step);
    trackOnboarding('step_viewed', {}, step);
  }, [step, loading]);

  useEffect(() => {
    if (source) trackOnboarding('knowledge_method', { method: source });
  }, [source]);


  useEffect(() => {
    const publicUrl = agentPublicUrl(agent);
    if (!publicUrl) {
      setQrDataUrl('');
      return;
    }
    QRCode.toDataURL(publicUrl, {
      errorCorrectionLevel: 'H',
      margin: 2,
      width: 420,
      color: { dark: '#111827', light: '#ffffff' },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
  }, [agent]);

  // The name field is disabled until a business type is chosen, so choosing
  // one is what makes it usable. Move the cursor there rather than leaving the
  // customer to hunt for the next thing to fill in.
  useEffect(() => {
    if (selectedTemplate && agentNameRef.current) {
      agentNameRef.current.focus();
      agentNameRef.current.select();
    }
  }, [selectedTemplate]);

  const summariseKnowledge = (payload) => {
    const chunks = Number(
      payload?.result?.stats?.chunks_created
      ?? payload?.response?.document?.chunks
      ?? payload?.document?.chunks
      ?? 0
    );
    const pages = Number(payload?.result?.ingest_health?.pages_embedded ?? 0);
    return chunks > 0 ? { chunks, pages } : null;
  };

  const knowledgeComplete = async (payload) => {
    // "Did that actually work?" is where people give up. Say what was learned
    // rather than moving the screen on silently.
    const summary = summariseKnowledge(payload);
    setKnowledgeSummary(summary);
    trackOnboarding('knowledge_saved', { method: source, chunks: summary?.chunks ?? 0, pages: summary?.pages ?? 0 });
    setError('');
    await showPreview(latestCollection.current);
  };

  // What the agent would answer from, before it exists. Chunk counts cannot
  // tell a customer's own site from a parked domain or someone else's; only
  // the customer can. Whether it is enough is the server's decision -- the one
  // agent creation applies -- so a reload cannot change the answer.
  const showPreview = async (target = collection) => {
    setPreviewFailed(false);
    try {
      const response = await agentsAPI.getKnowledgePreview(target?.name || target?.collection_name);
      const data = response.data || {};
      if (data.status === 'blocked') {
        trackOnboarding('knowledge_rejected', { method: source, verdict: 'blocked', chunks: data.chunks ?? 0 });
        setSource(null);
        setError(data.reason || 'Nothing searchable was found. Add your website, paste your details, or upload a document.');
        setStep('knowledge');
        return;
      }
      trackOnboarding('knowledge_previewed', { status: data.status, warnings: (data.warnings || []).length });
      setError('');
      setPreview(data);
      setStep('preview');
    } catch (err) {
      if (err.response?.status === 501) {
        // No vector store to sample on this deployment. Do not strand the
        // customer on a preview that cannot exist.
        await continueAfterKnowledge();
        return;
      }
      setPreviewFailed(true);
      setError(err.response?.data?.detail || 'Could not load a preview of your knowledge. Try again.');
    }
  };

  // A workspace that already has an agent -- created before knowledge came
  // first, or resumed mid-setup -- has nothing left but to finish. Everyone
  // else creates theirs now that there is something for it to read.
  const continueAfterKnowledge = async () => {
    if (isAdditionalAgent) {
      setPreview(null);
      setStep('agent');
      return;
    }
    let existingAgent = agent;
    if (!existingAgent && onboardingProgress?.agent_ready) {
      try {
        const response = await agentsAPI.list();
        existingAgent = response.data?.agents?.[0];
        if (existingAgent) setAgent(existingAgent);
      } catch {
        // The knowledge save succeeded. A reload re-reads the durable server
        // state, so do not pretend the content was lost.
      }
    }
    setPreview(null);
    setStep(existingAgent ? 'finish' : 'agent');
  };

  const shareKnowledge = (option) => {
    setCollection(option.collection);
    setSharedWith(option.agents);
    setKnowledgeSummary(null);
    setSource(null);
    setError('');
    setStep('agent');
  };

  // Back from "share": whatever is added next goes to this agent's own
  // collection, not the one the customer was about to share.
  const revisitKnowledge = () => {
    if (isAdditionalAgent) {
      setCollection(ownCollection);
      setSharedWith([]);
    }
    setStep('knowledge');
  };

  const newCollection = async () => {
    const description = 'Website and document knowledge for the AI agent';
    const tenantName = (user?.tenant_name || '').trim();
    if (!isAdditionalAgent) {
      try {
        const created = await ragService.createCollection({
          name: tenantName ? `${tenantName} Website Knowledge` : 'Website Knowledge',
          description,
        });
        return created.collection;
      } catch (createError) {
        // A duplicate can only happen if setup is open twice. Re-read the
        // existing collection instead of creating another one.
        const { collections = [] } = await ragService.getCollections();
        const existing = collections.find((item) => item.is_default) || collections[0];
        if (!existing) throw createError;
        return existing;
      }
    }
    const baseName = tenantName.slice(0, 60) || 'Website';
    const agentCount = capacity?.agent_count || 0;
    for (let attempt = 0; ; attempt += 1) {
      try {
        const created = await ragService.createCollection({
          name: `${baseName} Agent ${agentCount + 1 + attempt} Knowledge`,
          description,
        });
        return created.collection;
      } catch (createError) {
        if (createError.response?.status === 403 || attempt === 4) throw createError;
      }
    }
  };

  // Opening setup, choosing a source or sharing creates nothing: abandoned
  // visits left collections behind. Content arriving creates one, once per
  // attempt, however many times submit is pressed.
  const collectionForSubmit = () => {
    if (latestCollection.current) return Promise.resolve(latestCollection.current);
    if (!pendingCollection.current) {
      pendingCollection.current = newCollection()
        .then((created) => {
          latestCollection.current = created;
          setCollection(created);
          if (isAdditionalAgent) setOwnCollection(created);
          return created;
        })
        .finally(() => { pendingCollection.current = null; });
    }
    return pendingCollection.current;
  };

  // Only for a workspace name that is itself wrong. One workspace can run several
  // businesses, so this is never the fix for content that names another one.
  const renameAndRecheck = async (event) => {
    event.preventDefault();
    setRenaming(true);
    setError('');
    try {
      const saved = await renameWorkspace(user?.tenant_id, workspaceName.trim());
      updateUserProfile?.({ tenant_name: saved.name });
      await showPreview(latestCollection.current);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not rename your workspace. Try again.');
    } finally {
      setRenaming(false);
    }
  };

  const continueUnfinished = async (item) => {
    latestCollection.current = item;
    setCollection(item);
    setOwnCollection(item);
    setError('');
    await showPreview(item);
  };

  const discardUnfinished = async (item) => {
    setDiscarding(true);
    setError('');
    try {
      await ragService.deleteCollection(item.id);
      setUnfinished((current) => current.filter((entry) => entry.collection.id !== item.id));
      if (latestCollection.current?.id === item.id) {
        latestCollection.current = null;
        setCollection(null);
        setOwnCollection(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not remove that content. Try again.');
    } finally {
      setDiscarding(false);
    }
  };

  // "No" has to remove what was ingested, or a wrong site stays in the
  // collection and the agent answers from it anyway. The preview shows the
  // whole collection, so all of it goes. The API refuses this while any agent
  // uses the collection, and says why. Nothing replaces it until there is new
  // content to put there.
  const discardKnowledge = async () => {
    if (!collection) return;
    setDiscarding(true);
    setError('');
    try {
      await ragService.deleteCollection(collection.id);
      pendingCollection.current = null;
      latestCollection.current = null;
      setCollection(null);
      if (isAdditionalAgent) setOwnCollection(null);
      setUnfinished((current) => current.filter((entry) => entry.collection.id !== collection.id));
      trackOnboarding('knowledge_discarded', { status: preview?.status ?? null });
      setPreview(null);
      setKnowledgeSummary(null);
      setSource(null);
      setStep('knowledge');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not remove that content. Try again.');
    } finally {
      setDiscarding(false);
    }
  };

  // Confirming the content is the last thing a first agent needs: the name,
  // alert email and settings are already filled in, so "yes" creates it.
  // A separate create screen after this lost people who had finished
  // everything but one more click.
  const hasAgent = Boolean(agent || onboardingProgress?.agent_ready);
  const confirmKnowledge = async () => {
    trackOnboarding('knowledge_confirmed', { status: preview?.status ?? null });
    if (hasAgent || isAdditionalAgent || !notificationEmail) {
      await continueAfterKnowledge();
      return;
    }
    await createAgent();
  };

  const createAgent = async (event) => {
    event?.preventDefault();
    const name = agentName.trim().slice(0, AGENT_NAME_MAX).trim();
    const email = notificationEmail.trim().toLowerCase();
    const selectedVertical = ONBOARDING_VERTICALS.find(({ template }) => template === selectedTemplate);
    if (!name || !collection || !selectedVertical) {
      // Submitting does nothing visible here, so this is the only record of it.
      trackOnboarding('agent_form_invalid', {
        missing: [!name && 'name', !collection && 'collection', !selectedVertical && 'business_type']
          .filter(Boolean).join(','),
      });
      return;
    }
    if (!isAdditionalAgent && !/\S+@\S+\.\S+/.test(email)) {
      trackOnboarding('agent_form_invalid', { missing: 'email' });
      setError('Enter a valid notification email address.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (!isAdditionalAgent) {
        await updateNotificationContacts(user.tenant_id, {
          notification_emails: [{ email, name: 'Primary contact' }],
          notification_phones: [],
          notification_preferences: {
            support_email: email,
            support_tickets_enabled: true,
          },
        });
      }

      // The API resolves this template itself. The browser sends only the
      // customer's business selection and never receives the prompt body.
      const defaultsResponse = await agentsAPI.getAgentDefaults();
      const defaults = defaultsResponse.data || {};

      const created = await agentsAPI.createAgent({
        name,
        type: 'chat',
        provider: '',
        model: '',
        prompt_template_name: selectedVertical.template,
        // null tells the API to use the selected template's own temperature.
        temperature: null,
        max_tokens: defaults.max_tokens ?? 2000,
        top_p: defaults.top_p ?? 1,
        frequency_penalty: defaults.frequency_penalty ?? 0,
        presence_penalty: defaults.presence_penalty ?? 0,
        use_rag: true,
        rag_config: {
          // Agent configuration stores the display name. The prompt generator
          // adds the tenant prefix when it resolves the physical collection.
          collection_name: collection.name || collection.collection_name,
          top_k: defaults.rag_top_k ?? 8,
          score_threshold: defaults.rag_score_threshold ?? 0.5,
        },
      });
      // Everything after this step is a link or QR code for this agent.
      if (!hasAgentId(created.data)) throw new Error('Your AI agent could not be created. Try again.');
      setAgent({ ...created.data, name });
      trackOnboarding('agent_created', { template: selectedVertical.template, additional_agent: isAdditionalAgent });
      // Knowledge came first, so the agent was the last thing missing.
      setStep('finish');
    } catch (err) {
      trackOnboarding('agent_create_failed', {
        status: err.response?.status ?? null,
        detail: err.response?.data?.detail || err.message,
      });
      setError(err.response?.data?.detail || err.message || 'Could not create your AI agent.');
    } finally {
      setSaving(false);
    }
  };

  const widgetCode = hasAgentId(agent) ? buildWidgetCode(agent.id) : '';
  const currentStep = step === 'knowledge' ? 1 : step === 'preview' || step === 'agent' ? 2 : 3;
  // Nobody should install an agent they have never seen answer. This is also
  // the cheapest way to expose thin knowledge: if it cannot say what the
  // business does, the content went in but is not usable.
  const finishOnboarding = () => {
    // Sent before navigating: off the setup page, events are no longer recorded.
    trackOnboarding('setup_finished', { additional_agent: isAdditionalAgent });
    flushOnboardingEvents();
    // Release TenantLayout's navigation gate only when the customer finishes
    // the final install/share screen. The database remains the source of truth
    // on later visits; this event only updates the current tab before routing.
    if (!isAdditionalAgent) {
      window.dispatchEvent(new CustomEvent('serviti:onboarding-complete', {
        detail: { tenantId: user.tenant_id },
      }));
    }
    navigate(
      `/t/${user.tenant_id}/${isAdditionalAgent ? 'agent-management' : 'dashboard'}`,
      { replace: true }
    );
  };

  if (isPlatformWorkspace) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-950">Open a tenant&apos;s workspace first</h1>
        <p className="mt-3 text-sm text-gray-600">
          Setup adds knowledge and agents to one workspace, and the platform account is not one. Open the tenant from the Tenants page, then run setup there.
        </p>
        <button type="button" onClick={() => navigate('/platform/tenants')} className="mt-6 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Go to Tenants</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
          <p className="mt-4 text-sm text-gray-600">Preparing your workspace…</p>
        </div>
      </div>
    );
  }

  // Capacity prevents creating ANOTHER agent. It must never stop someone
  // finishing the one they already have: that agent cannot answer anything
  // until the knowledge step is done.
  const blockedByCapacity = Boolean(
    capacity
    && !capacity.can_create_agent
    && (requestedAdditionalAgent || !onboardingProgress?.agent_ready)
  );

  if (blockedByCapacity) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <SparklesIcon className="mx-auto h-12 w-12 text-amber-500" />
        <h1 className="mt-4 text-2xl font-bold text-gray-950">Your AI Agent limit is reached</h1>
        <p className="mt-3 text-sm text-gray-600">
          This workspace is using {capacity.agent_count} of {capacity.max_agents} AI Agents included with its plan.
          Remove an agent or upgrade the plan before creating another.
        </p>
        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
          <button type="button" onClick={() => navigate(`/t/${user.tenant_id}/agent-management`)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50">Manage agents</button>
          <button type="button" onClick={() => navigate(`/t/${user.tenant_id}/billing`)} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">View plans</button>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-950">We could not prepare your setup</h1>
        <p className="mt-3 text-sm text-red-700">{error || 'Could not prepare your knowledge base.'}</p>
        <button type="button" onClick={() => window.location.reload()} className="mt-6 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Try again</button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm font-semibold text-blue-600">
          <SparklesIcon className="h-5 w-5" />
          {isAdditionalAgent ? 'Create another AI Agent' : 'Set up your first AI Agent'}
        </div>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-950">
          {currentStep === 1 && 'First, add your business knowledge'}
          {currentStep === 2 && (step === 'agent' ? 'Now create your AI Agent' : 'Check your business information')}
          {currentStep === 3 && 'Your AI Agent is ready'}
        </h1>
        <p className="mt-2 text-gray-600">
          Finish the highlighted step below. Your workspace menu unlocks after your knowledge and first agent are ready.
        </p>
        {capacity && (
          <p className="mt-2 text-sm font-medium text-gray-500">
            {capacity.agent_count} of {capacity.max_agents} AI Agent slots used · {capacity.remaining} available
          </p>
        )}
        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3" aria-label="Setup progress">
          {/* A first agent is created by confirming its content, so step 2 is
              that check. The create screen only remains for an extra agent or
              where no preview can be shown. */}
          {['Add knowledge', usedCreateScreen || step === 'agent' ? 'Create your agent' : 'Check it’s your business', 'Try & share'].map((label, index) => {
            const number = index + 1;
            const complete = currentStep > number;
            const active = currentStep === number;
            // Knowledge can be revisited until the agent exists; later steps
            // stay put, since going back past agent creation would repeat it.
            const canRevisit = complete && number === 1 && step === 'agent';
            const className = `rounded-xl border px-4 py-3 text-left text-sm font-semibold ${
              active ? 'border-blue-500 bg-blue-600 text-white shadow-md ring-4 ring-blue-100' : complete ? 'border-green-200 bg-green-50 text-green-800' : 'border-gray-200 bg-gray-50 text-gray-500'
            }`;
            const content = (
              <>
                {complete && <CheckCircleIcon className="mr-1.5 inline h-5 w-5" aria-hidden="true" />}
                {number}. {label}
                {complete && <span className="sr-only"> (done)</span>}
              </>
            );
            return canRevisit ? (
              <button key={label} type="button" onClick={revisitKnowledge} className={`${className} hover:bg-green-100`}>
                {content}
                <span className="ml-1 font-normal underline">Change</span>
              </button>
            ) : (
              <div key={label} aria-current={active ? 'step' : undefined} className={className}>
                {content}
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
          {previewFailed && (
            <button type="button" onClick={() => showPreview()} className="ml-2 font-semibold underline">Check again</button>
          )}
        </div>
      )}

      {step === 'knowledge' && (
        <div className="rounded-2xl border-2 border-blue-400 bg-white p-5 shadow-xl ring-4 ring-blue-100 sm:p-7">
          {!source ? (
            <div>
              <div className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-800">Do this now</div>
              <h2 className="mt-3 text-2xl font-bold text-gray-950">Choose one way to add business knowledge</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">One useful page or document is enough to continue. You can add more after setup.</p>
              {isAdditionalAgent && unfinished.length > 0 && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-bold text-gray-950">Unfinished imports</h3>
                  <p className="mt-1 text-sm text-gray-600">No agent uses these yet. New content below goes into its own place, not into these.</p>
                  <ul className="mt-3 space-y-2">
                    {unfinished.map(({ collection: item, importing }) => (
                      <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm">
                        <span className="font-semibold text-gray-900">{item.name}</span>
                        {importing ? (
                          <span className="text-gray-600">Still importing. Check back when it finishes.</span>
                        ) : (
                          <span className="flex gap-2">
                            <button type="button" onClick={() => continueUnfinished(item)} disabled={discarding} aria-label={`Continue with ${item.name}`} className="rounded-lg border border-blue-300 px-3 py-1.5 font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60">Continue</button>
                            <button type="button" onClick={() => discardUnfinished(item)} disabled={discarding} aria-label={`Discard ${item.name}`} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60">Discard</button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-6 grid gap-4 lg:grid-cols-3">
                {isAdditionalAgent && sharedOptions.map((option) => (
                  <button key={option.collection.id || option.collection.name} type="button" onClick={() => shareKnowledge(option)} className="rounded-xl border-2 border-green-200 bg-green-50 p-6 text-left transition hover:border-green-500">
                    <SparklesIcon className="h-9 w-9 text-green-600" />
                    <div className="mt-4 text-lg font-bold text-gray-950">Share {option.agents.join(', ')}&apos;s knowledge</div>
                    <p className="mt-1 text-sm text-gray-600">This agent will answer from the same content. Anything added later reaches both.</p>
                  </button>
                ))}
                <button type="button" onClick={() => setSource('scrape')} className="relative min-h-[190px] rounded-xl border-2 border-blue-500 bg-blue-50 p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-100 focus:outline-none focus:ring-4 focus:ring-blue-200">
                  {!signupWebsite.unreadable && <span className="absolute right-4 top-4 rounded-full bg-blue-700 px-2.5 py-1 text-xs font-bold text-white">Recommended</span>}
                  <GlobeAltIcon className="h-9 w-9 text-blue-600" />
                  <div className="mt-4 text-lg font-bold text-gray-950">Use my website</div>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    {signupWebsite.prefill
                      ? 'Your signup website is already filled in. Serviti reads the useful pages for you.'
                      : 'Enter your website address. Serviti reads the useful pages for you.'}
                  </p>
                </button>
                <button type="button" onClick={() => setSource('paste')} className="relative min-h-[190px] rounded-xl border-2 border-amber-300 bg-amber-50 p-6 text-left transition hover:-translate-y-0.5 hover:border-amber-500 hover:bg-amber-100 focus:outline-none focus:ring-4 focus:ring-amber-100">
                  {signupWebsite.unreadable && <span className="absolute right-4 top-4 rounded-full bg-amber-700 px-2.5 py-1 text-xs font-bold text-white">Recommended</span>}
                  <ClipboardDocumentIcon className="h-9 w-9 text-amber-700" />
                  <div className="mt-4 text-lg font-bold text-gray-950">Paste business details</div>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    {signupWebsite.unreadable
                      ? 'Social pages can\u2019t be read automatically, so paste '
                      : 'Fastest if you have no website. Paste '}
                    <strong className="font-semibold text-gray-950">services, prices, hours, and common questions</strong>.
                  </p>
                </button>
                <button type="button" onClick={() => setSource('upload')} className="min-h-[190px] rounded-xl border-2 border-violet-200 bg-violet-50 p-6 text-left transition hover:-translate-y-0.5 hover:border-violet-500 hover:bg-violet-100 focus:outline-none focus:ring-4 focus:ring-violet-100">
                  <CloudArrowUpIcon className="h-9 w-9 text-violet-700" />
                  <div className="mt-4 text-lg font-bold text-gray-950">Upload a document</div>
                  <p className="mt-1 text-sm leading-6 text-gray-600">Add a PDF, Word file, brochure, price list, or FAQ.</p>
                </button>
              </div>
              <p className="mt-5 rounded-xl bg-gray-50 px-4 py-3 text-center text-sm font-medium text-gray-700">
                The next step opens automatically after Serviti creates searchable knowledge.
              </p>
            </div>
          ) : (
            <div>
              <button type="button" onClick={() => setSource(null)} className="mb-5 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800">
                <ArrowLeftIcon className="h-4 w-4" /> Choose a different source
              </button>
              {source === 'scrape' ? (
                <WebScraper
                  initialCollectionId={collection?.id}
                  resolveCollection={collectionForSubmit}
                  embedded
                  initialUrl={signupWebsite.prefill}
                  onComplete={knowledgeComplete}
                />
              ) : source === 'upload' ? (
                <Upload initialCollectionId={collection?.id} resolveCollection={collectionForSubmit} embedded onComplete={knowledgeComplete} />
              ) : (
                <PasteContentRecovery collectionId={collection?.id} resolveCollection={collectionForSubmit} standalone initialContent={isAdditionalAgent ? '' : onboardingProgress?.business_description || ''} onSaved={knowledgeComplete} />
              )}
            </div>
          )}
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="rounded-2xl border-2 border-blue-400 bg-white p-5 shadow-xl ring-4 ring-blue-100 sm:p-7">
          <div className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-800">Check this</div>
          <h2 className="mt-3 text-2xl font-bold text-gray-950">Is this your business?</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            This is what we found. Your AI agent will answer your customers using it.
          </p>
          {preview.status === 'limited' && (
            <div role="status" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
              {preview.reason}
            </div>
          )}
          {(preview.warnings || []).map((warning) => (
            <div key={warning} role="status" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
              {warning}
            </div>
          ))}
          {preview.warnings?.length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer font-medium text-gray-700">Is the workspace name itself wrong? Rename it</summary>
              <form onSubmit={renameAndRecheck} className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label htmlFor="workspace-name" className="block text-sm font-semibold text-gray-700">Workspace name</label>
                <input id="workspace-name" type="text" maxLength={100} value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} className="mt-1 min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <button type="submit" disabled={renaming || !workspaceName.trim()} className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60">{renaming ? 'Saving…' : 'Rename workspace'}</button>
              </form>
            </details>
          )}
          {preview.sources?.length > 0 && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold text-gray-900">Where it came from</h3>
              <ul className="mt-2 space-y-1 text-sm text-gray-700">
                {preview.sources.map(({ label, title }) => (
                  <li key={`${label}|${title}`} className="break-words">
                    {title && <span className="font-medium text-gray-900">{title}</span>}
                    {title && label ? ' — ' : ''}
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {preview.excerpts?.length > 0 && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold text-gray-900">What it says</h3>
              <ul className="mt-2 space-y-2">
                {preview.excerpts.map((excerpt) => (
                  <li key={excerpt} className="break-words rounded-lg bg-gray-50 px-3 py-2 text-sm leading-6 text-gray-700">“{excerpt}”</li>
                ))}
              </ul>
            </div>
          )}
          {!hasAgent && (
            // Kept here, not defaulted silently: the type decides how the agent
            // behaves (a hotel gets a dates form, a school its admissions flow).
            <div className="mt-5">
              <label htmlFor="preview-business-type" className="block text-sm font-semibold text-gray-900">What kind of business is this?</label>
              <select id="preview-business-type" value={selectedTemplate} onChange={(event) => setSelectedTemplate(event.target.value)} className="mt-2 min-h-[48px] w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 text-base text-gray-950 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:max-w-md">
                {ONBOARDING_VERTICALS.map(({ template, label }) => <option key={template} value={template}>{label}</option>)}
              </select>
              {notificationEmail && !isAdditionalAgent && (
                <p className="mt-2 text-sm text-gray-600">New enquiries will be emailed to <span className="font-semibold text-gray-900">{notificationEmail}</span>.</p>
              )}
            </div>
          )}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={confirmKnowledge} disabled={discarding || saving} className="min-h-[48px] rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Creating your AI agent…' : hasAgent ? 'Yes, this is my business' : 'Yes, create my AI agent'}
            </button>
            <button type="button" onClick={discardKnowledge} disabled={discarding || saving} className="min-h-[48px] rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60">{discarding ? 'Removing…' : 'No, this is wrong. Remove it'}</button>
          </div>
        </div>
      )}

      {step === 'agent' && (
        <div className="mx-auto max-w-xl rounded-2xl border-2 border-blue-400 bg-white p-6 shadow-xl ring-4 ring-blue-100 sm:p-7">
          <div className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-800">Do this now</div>
          <h2 className="mt-3 text-2xl font-bold text-gray-950">Your agent is ready to create</h2>
          {knowledgeSummary && (
            <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm font-semibold text-green-800">
              Knowledge saved — {knowledgeSummary.chunks} searchable section{knowledgeSummary.chunks === 1 ? '' : 's'}
              {knowledgeSummary.pages > 0 && ` from ${knowledgeSummary.pages} page${knowledgeSummary.pages === 1 ? '' : 's'}`}.
            </p>
          )}
          <p className="mt-2 text-sm leading-6 text-gray-600">We’ve filled in the settings. Create your agent, then ask it a real customer question. You can change these settings later.</p>
          <form onSubmit={createAgent} className="mt-6 space-y-5">
            <details>
              <summary className="min-h-[44px] cursor-pointer text-sm font-medium text-blue-700">Change agent name or business type (optional)</summary>
            <div className={!selectedTemplate ? 'rounded-xl bg-amber-50 p-4 ring-4 ring-amber-200' : ''}>
              <label htmlFor="first-agent-business-type" className="block text-sm font-semibold text-gray-700">What best describes your business?</label>
              <select id="first-agent-business-type" required value={selectedTemplate} onChange={(event) => setSelectedTemplate(event.target.value)} className="mt-2 min-h-[52px] w-full rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-base text-gray-950 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="" disabled>Select your business type</option>
                {ONBOARDING_VERTICALS.map(({ template, label }) => <option key={template} value={template}>{label}</option>)}
              </select>
            </div>
            <div className={!selectedTemplate ? 'opacity-45' : ''}>
              <label htmlFor="first-agent-name" className="block text-sm font-semibold text-gray-700">AI Agent Name</label>
              <input ref={agentNameRef} id="first-agent-name" type="text" required disabled={!selectedTemplate} maxLength={100} value={agentName} onChange={(event) => setAgentName(event.target.value)} placeholder={`${user?.tenant_name || 'My Business'} AI Agent`} className="mt-2 min-h-[52px] w-full rounded-lg border border-gray-300 px-4 py-3 text-base text-gray-950 caret-blue-600 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100" />
              <p className="mt-1 text-xs text-gray-500">You can change this name now or later.</p>
            </div>
            </details>
            {isAdditionalAgent ? (
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                This agent will use the workspace&apos;s existing lead and support notification settings.
                {sharedWith.length > 0 && (
                  <p className="mt-2 font-semibold">
                    It will answer from the same knowledge as {sharedWith.join(', ')}.
                  </p>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="first-agent-notification-email" className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <EnvelopeIcon className="h-5 w-5 text-blue-600" /> Lead &amp; Support Notification Email
                  </label>
                  <input id="first-agent-notification-email" type="email" required readOnly maxLength={254} value={notificationEmail} placeholder="you@company.com" className="mt-2 w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-700 shadow-sm" />
                  <p className="mt-1 text-xs text-gray-500">New enquiry, lead, and support-ticket alerts go to the email you verified during signup.</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Email alerts are active immediately. Add WhatsApp alert numbers any time under Team &amp; notifications → Lead alerts.
                </div>
              </>
            )}
            <button type="submit" disabled={saving || !selectedTemplate || !agentName.trim() || (!isAdditionalAgent && !notificationEmail.trim())} className="min-h-[52px] w-full rounded-xl bg-blue-700 px-5 py-3 text-base font-bold text-white shadow-md hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-45">
              {saving ? 'Creating your agent…' : 'Create my AI Agent'}
            </button>
          </form>
        </div>
      )}

      {step === 'finish' && agent && (
        <div className="rounded-2xl border border-green-200 bg-white p-6 shadow-sm sm:p-8">
          {knowledgeSummary && (
            <div role="status" className="mb-4 rounded-lg border-2 border-green-300 bg-green-50 px-4 py-3 text-sm font-semibold text-green-900">
              Knowledge saved — {knowledgeSummary.chunks} searchable section{knowledgeSummary.chunks === 1 ? '' : 's'}
              {knowledgeSummary.pages > 0 && ` from ${knowledgeSummary.pages} page${knowledgeSummary.pages === 1 ? '' : 's'}`}.
            </div>
          )}
          <div className="text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600" />
            <h2 className="mt-3 text-2xl font-bold text-gray-950">{agent.name} is ready</h2>
            <p className="mt-2 text-sm text-gray-600">Preview it now, save the install options, then finish setup to unlock your workspace.</p>
          </div>

          <div className="mt-6 rounded-xl border-2 border-blue-400 bg-blue-50 p-5 ring-4 ring-blue-100">
            <h3 className="text-lg font-bold text-gray-950">Your AI agent is ready. Check it out.</h3>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              It opens in a new tab, exactly as your customers will see it. Ask it what a customer would ask.
              If it cannot answer, add more business details before sharing it.
            </p>
            <a
              href={getTrialAgentUrl(agent)}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackOnboarding('trial_opened', {})}
              className="mt-4 inline-flex min-h-[48px] items-center justify-center rounded-lg bg-blue-700 px-6 font-semibold text-white hover:bg-blue-800"
            >
              Check it out
            </a>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="rounded-xl border border-gray-200 bg-gray-950 p-5 text-gray-100">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-semibold"><CodeBracketIcon className="h-5 w-5" /> Widget code</div>
                <button type="button" onClick={() => { navigator.clipboard.writeText(widgetCode); toast.success('Widget code copied'); }} className="inline-flex items-center gap-1 rounded-md bg-white/10 px-3 py-1.5 text-sm hover:bg-white/20">
                  <ClipboardDocumentIcon className="h-4 w-4" /> Copy
                </button>
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all text-xs leading-6"><code>{widgetCode}</code></pre>
              <button type="button" onClick={() => downloadText('serviti-widget-code.txt', widgetCode)} className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">
                Download widget code
              </button>
            </div>

            <div className="rounded-xl border border-gray-200 p-5 text-center">
              <div className="flex items-center justify-center gap-2 font-semibold text-gray-900"><QrCodeIcon className="h-5 w-5" /> QR code</div>
              {qrDataUrl ? <img src={qrDataUrl} alt={`QR code for ${agent.name}`} className="mx-auto mt-3 h-52 w-52" /> : <div className="mx-auto mt-3 h-52 w-52 animate-pulse rounded bg-gray-100" />}
              <a href={qrDataUrl || undefined} download={`${agent.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-qr.png`} className={`mt-4 block rounded-lg border border-blue-600 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 ${!qrDataUrl ? 'pointer-events-none opacity-50' : ''}`}>
                Download QR code
              </a>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button type="button" onClick={finishOnboarding} className="min-h-[48px] rounded-lg bg-green-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:bg-green-700">{isAdditionalAgent ? 'Go to Agent Management' : 'Finish setup and open dashboard'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirstTimeSetupPage;
